/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import moment from 'moment';
import { parseExpression } from 'cron-parser';
import { Button, Checkbox, FormControlLabel, Grid, makeStyles } from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import CloseIcon from '@material-ui/icons/Close';
import { useDispatch } from 'react-redux';
import Theme from '../ui/Theme';
import PopupText from '../common/PopupText';
import PopupTextField from '../common/PopupTextField';
import { showProgress } from '../../redux/actions/DealershipActions';
import MultiSelectSearch from '../common/search/MultiSelectSearch';
import service from '../../apis/service';
import { ErrorToast, MultipleErrorToast, notifySuccess } from '../utils/Utils';
import PopupDropDown from '../common/PopupDropDown';
import { apiEndpoints } from '../../constants/apiEndpoints';

const customStyles = {
  content: {
    height: '700px',
    width: '800px',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
  },
};
const useStyles = makeStyles((theme) => ({
  root: {
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  popupHeading: {
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: '22px',
    fontStyle: 'normal',
    marginBottom: '10px',
    marginTop: '0px',
  },
  popSubmitBtn: {
    padding: '0 70px',
    height: '45px',
    width: '200px',
  },
  popCancelBtn: {
    padding: '0 70px',
    height: '45px',
    width: '200px',
    marginRight: '28px',
  },
  popupMainContainer: {
    marginTop: '43px',
  },
  popupCloseBtn: {
    borderRadius: '50%',
    backgroundColor: Theme.palette.secondary.main,
    padding: '8px',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
    },
    cursor: 'pointer',
  },
  popupLabels: {
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '15px',
    fontStyle: 'normal',
    color: 'rgba(0, 0, 0, 0.4)',
    textTransform: 'uppercase',
    textAlign: 'left',
  },
  btnUploadStyle: {
    height: '24px',
    background: Theme.palette.secondary.main,
    width: '100%',
    marginBottom: '10px',
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: '600',
    fontSize: '12px',
    lineHeight: '15px',
    textAlign: 'center',
    letterSpacing: '0.2px',
    color: Theme.palette.quad.main,
  },
  publishDateField: {
    backgroundColor: Theme.palette.secondary.main,
    height: '35px',
    width: '290px',
    padding: '5px 10px',
    marginLeft: '0px',
    marginTop: '10px',
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: '14px',
    lineHeight: '17px',
    marginBottom: '10px',
  },
  popInputs: {
    border: 0,
    outline: 0,
    backgroundColor: Theme.palette.secondary.main,
    height: '45px',
    width: '343px',
    padding: '0 10px',
    marginLeft: '0px',
    marginTop: '10px',
    marginBottom: '20px',
    '&:placeholder': {
      color: '#4b4b4b',
      opacity: '1',
    },
  },
}));

const InappAddNewPopup = ({ close, add, selectedData, refreshData }) => {
  const classes = useStyles();
  const [data, setData] = useState({});
  const [selectedDealership, setSelectedDealership] = useState([]);
  const [dealershipList, setDealershipList] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!add) {
      setData({
        ...selectedData,
        staticProps: selectedData.staticProps ? JSON.stringify(selectedData.staticProps, undefined, 2) : '',
        dealerships: selectedData.dealerships
          ? selectedData.dealerships.map((d) => d && { label: d.name, value: d.id })
          : [],
      });
      setSelectedDealership(
        (selectedData.dealerships || []).filter((d) => d).map((d) => ({ label: d.name, value: d.id })),
      );
    } else {
      setData({
        expiry: moment().format('YYYY-MM-DD'),
      });
    }
  }, [add, selectedData]);

  useEffect(() => {
    const fetchDealershipList = async () => {
      dispatch(showProgress(true));
      await service
        .post(apiEndpoints.GET_DEALERSHIPS_LIST, {
          filters: { sort: { key: 'createdAt', order: 'DESC' }, enabled: 'ENABLED' },
        })
        .then((response) => {
          setDealershipList(response?.map((r) => ({ value: r.id, label: r.name })) || []);
          dispatch(showProgress(false));
        })
        .catch((error) => {
          ErrorToast(error);
          dispatch(showProgress(false));
        });
    };
    fetchDealershipList();
  }, [dispatch]);

  const handleDropdownChange = (value) => {
    setSelectedDealership(value);
    setData({ ...data, dealerships: value });
  };

  const handleDataChange = ({ target }) => {
    const { name: key, value } = target;
    if (key === 'type') {
      setData({
        ...data,
        [key]: value,
        subType: selectedData?.subType || '',
        contentUrl: selectedData?.contentUrl || '',
      });
    } else {
      setData({ ...data, [key]: value });
    }
  };

  const handleCheckboxChange = ({ target }) => {
    setData({ ...data, [target.name]: target.checked });
  };

  const handleDateChange = (label, date) => {
    setData({ ...data, [label]: date });
  };

  const validateForm = () => {
    const errors = [];
    let formIsValid = true;
    if (!data.title) {
      formIsValid = false;
      errors.push('Please enter title of inapp.');
    }
    if (!data.type) {
      formIsValid = false;
      errors.push('Please enter type of inapp.');
    } else if (data.type === 'VIDEO' && (!data.subType || !data.contentUrl)) {
      formIsValid = false;
      errors.push('Please enter custom subtype and content url of inapp.');
    } else if ((data.type === 'CUSTOM' || data.type === 'FEATURE_INTRO') && !data.subType) {
      formIsValid = false;
      errors.push('Please enter custom subtype of inapp.');
    } else if (data.type !== 'CUSTOM' && data.type !== 'FEATURE_INTRO' && !data.contentUrl) {
      formIsValid = false;
      errors.push(`Please enter ${data.type} content url.`);
    }
    if (!data.expiry) {
      formIsValid = false;
      errors.push('Please enter expiry for inapp.');
    }
    if (data.staticProps) {
      try {
        JSON.parse(data.staticProps);
      } catch (ex) {
        formIsValid = false;
        errors.push('Please enter valid json for static props.');
      }
    }
    if (data.cronExpression) {
      try {
        parseExpression(data.cronExpression);
      } catch (ex) {
        formIsValid = false;
        errors.push('Please enter valid cron expression. Like * * * * *');
      }
    }

    if (!data.event) {
      formIsValid = false;
      errors.push('Please enter event of inapp.');
    }

    if (errors.length !== 0) {
      MultipleErrorToast(errors);
    }
    return formIsValid;
  };

  const saveInapp = async () => {
    const check = validateForm();
    const { title, expiry, type, enabled, description, contentUrl, subType, cronExpression, frequency, event } = data;
    if (check) {
      dispatch(showProgress(true));
      const staticProps = data.staticProps ? JSON.parse(data.staticProps) : {};
      const request = {
        title,
        expiry: moment(expiry).format('YYYY-MM-DD'),
        type,
        enabled: !!enabled,
        description: description ?? '',
        applicableFor:
          data.dealerships && data.dealerships.length
            ? data.dealerships.map((d) => d && d.value).filter((d) => d !== '*')
            : null,
        staticProps,
        ...(cronExpression ? { cronExpression } : {}),
        ...(frequency ? { frequency } : {}),
        ...(type === 'CUSTOM' || type === 'FEATURE_INTRO' ? { subType } : { contentUrl }),
        ...(type === 'VIDEO' ? { subType } : {}),
        event,
      };
      if (add) {
        await service
          .post(apiEndpoints.INAPP_FETCH, request)
          .then((response) => {
            refreshData(response.id);
            dispatch(showProgress(false));
            notifySuccess('Saved Successfully');
            close();
          })
          .catch((error) => {
            dispatch(showProgress(false));
            ErrorToast(error);
          });
      } else {
        await service
          .put(apiEndpoints.INAPP_UPDATE.replace('INAPP_ID', data.id), request)
          .then(() => {
            refreshData(data.id);
            dispatch(showProgress(false));
            notifySuccess('Saved Successfully');
            close();
          })
          .catch((error) => {
            dispatch(showProgress(false));
            ErrorToast(error);
          });
      }
    }
  };

  const getTextFields = () => {
    if (data.type === 'CUSTOM' || data.type === 'FEATURE_INTRO') {
      return (
        <>
          <Grid item>
            <PopupText text="Sub-type" required />
          </Grid>
          <Grid item>
            <PopupTextField
              name="subType"
              rows={1}
              placeholder="Sub-type"
              height="45"
              handleChange={handleDataChange}
              value={data.subType}
            />
          </Grid>
        </>
      );
    }
    if (data.type === 'VIDEO') {
      return (
        <>
          <Grid item>
            <PopupText text="Sub-type" required />
          </Grid>
          <Grid item>
            <PopupTextField
              name="subType"
              rows={1}
              placeholder="Sub-type"
              height="45"
              handleChange={handleDataChange}
              value={data.subType}
            />
          </Grid>
          <Grid item>
            <PopupText text="Content Url" required />
          </Grid>
          <Grid item>
            <PopupTextField
              name="contentUrl"
              rows={1}
              placeholder="Content Url"
              id="contenturl_id"
              height="45"
              handleChange={handleDataChange}
              value={data.contentUrl}
            />
          </Grid>
        </>
      );
    }
    return (
      <>
        <Grid item>
          <PopupText text="Content Url" required />
        </Grid>
        <Grid item>
          <PopupTextField
            name="contentUrl"
            rows={1}
            placeholder="Content Url"
            id="contenturl_id"
            height="45"
            handleChange={handleDataChange}
            value={data.contentUrl}
          />
        </Grid>
      </>
    );
  };

  return (
    <Modal isOpen onRequestClose={close} style={customStyles}>
      <Grid container direction="row" alignItems="center">
        <Grid item xs={11}>
          <Grid container direction="column" justifyContent="flex-start" alignItems="flex-start">
            <Grid item className={classes.popupHeading}>
              {add ? 'Add Inapp' : 'Edit Inapp'}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={1}>
          <CloseIcon className={classes.popupCloseBtn} onClick={close} />
        </Grid>
      </Grid>
      <Grid container direction="row">
        <Grid item xs={6}>
          <Grid container direction="column">
            <Grid item>
              <PopupText text="Inapp Name" required />
            </Grid>
            <Grid item>
              <PopupTextField
                name="title"
                rows={1}
                placeholder="Inapp Name"
                id="name_id"
                height="45"
                handleChange={handleDataChange}
                value={data.title}
              />
            </Grid>
            <Grid item>
              <PopupText text="Description" />
            </Grid>
            <Grid item>
              <PopupTextField
                name="description"
                rows={4}
                placeholder="Enter Description"
                id="description_id"
                height="45"
                handleChange={handleDataChange}
                value={data.description}
              />
            </Grid>
            <Grid item>
              <PopupText text="Type" required />
            </Grid>
            <Grid item>
              <PopupDropDown
                list={[
                  { id: 1, name: 'VIDEO' },
                  { id: 2, name: 'IFRAME' },
                  { id: 3, name: 'IMAGE' },
                  { id: 4, name: 'FEATURE_INTRO' },
                  { id: 5, name: 'CUSTOM' },
                ]}
                height="43px"
                width="343px"
                marginBottom="20px"
                name="type"
                handleChange={handleDataChange}
                value={data.type}
              />
            </Grid>
            {getTextFields()}
            <Grid item>
              <PopupText text="Expiry" required />
            </Grid>
            <Grid item>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  placeholder="YYYY-MM-DD"
                  value={data.expiry}
                  InputProps={{
                    disableUnderline: true,
                    classes: {
                      input: classes.publishDateField,
                    },
                  }}
                  onChange={(date) => handleDateChange('expiry', date)}
                  format="yyyy-MM-dd"
                />
              </MuiPickersUtilsProvider>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid container direction="column">
            <Grid item>
              <PopupText text="Dealerships" />
            </Grid>
            <Grid item style={{ marginBottom: '20px', marginTop: '10px' }}>
              <MultiSelectSearch
                placeholderButtonLabel="Select a Dealership"
                selectedOptions={selectedDealership}
                onChange={handleDropdownChange}
                options={dealershipList}
                name="dealerships"
                height="43px"
                width="343px"
                showValue
              />
            </Grid>
            <Grid item>
              <PopupText text="Event" required />
            </Grid>
            <Grid item>
              <PopupDropDown
                list={[
                  { id: 1, name: 'APP_OPEN' },
                  // { id: 2, name: 'HOME_VIEW' },
                  // { id: 3, name: 'COLLECTION_VIEW' },
                  // { id: 4, name: 'LEADERBOARD_VIEW' },
                  // { id: 5, name: 'REPORTS_VIEW' },
                  // { id: 6, name: 'ACCOUNT_VIEW' },
                ]}
                height="43px"
                width="343px"
                marginBottom="20px"
                name="event"
                handleChange={handleDataChange}
                value={data.event}
              />
            </Grid>
            <Grid item>
              <PopupText text="Cron Expression" />
            </Grid>
            <Grid item>
              <PopupTextField
                name="cronExpression"
                rows={1}
                placeholder="Cron Expression"
                id="cronexpression_id"
                height="45"
                handleChange={handleDataChange}
                value={data.cronExpression}
              />
            </Grid>
            <Grid item>
              <PopupText text="Frequency" />
            </Grid>
            <Grid item>
              <PopupTextField
                name="frequency"
                rows={1}
                placeholder="Frequency"
                id="frequency_id"
                height="45"
                handleChange={handleDataChange}
                value={data.frequency}
              />
            </Grid>
            <Grid item>
              <PopupText text="Static Props" />
            </Grid>
            <Grid item>
              <PopupTextField
                name="staticProps"
                rows={5}
                placeholder="Enter Static Props"
                id="staticprops_id"
                height="45"
                handleChange={handleDataChange}
                value={data.staticProps}
              />
            </Grid>
            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox checked={data.enabled} onChange={handleCheckboxChange} name="enabled" color="primary" />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container justifyContent="center" alignItems="center" style={{ marginTop: '20px' }}>
        <Button variant="contained" className={classes.popCancelBtn} onClick={close}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" className={classes.popSubmitBtn} onClick={saveInapp}>
          Save
        </Button>
      </Grid>
    </Modal>
  );
};

export default InappAddNewPopup;
